import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  endBefore,
  getCountFromServer,
  serverTimestamp,
  writeBatch
} from 'firebase/firestore'
import { db } from '../firebase'

const ASSIGNMENTS_COLLECTION = 'assignments'
const DEFAULT_LIST_LIMIT = 30
/** Limite pour modal / filtre devoirs par classe (évite les lectures excessives). */
export const ASSIGNMENTS_BY_CLASS_UI_LIMIT = 100
const BATCH_DELETE_SIZE = 450

/**
 * Liste des exerciseId présents dans un devoir (champs dénormalisés ou items).
 */
export function getExerciseIdsFromAssignment(assignment) {
  if (!assignment) return []
  if (Array.isArray(assignment.exerciseIds) && assignment.exerciseIds.length > 0) {
    return assignment.exerciseIds.map((id) => String(id).trim()).filter(Boolean)
  }
  const items = Array.isArray(assignment.items) ? assignment.items : []
  return items.map((item) => String(item?.exerciseId || '').trim()).filter(Boolean)
}

/**
 * Devoirs de la liste qui contiennent l'exerciseId donné.
 */
export function filterAssignmentsContainingExercise(assignments, exerciseId) {
  if (!exerciseId || !Array.isArray(assignments)) return []
  const id = String(exerciseId).trim()
  return assignments.filter((a) => getExerciseIdsFromAssignment(a).includes(id))
}

/**
 * Supprime tous les devoirs d'une classe (vérifie teacherId sur chaque document).
 * @returns {Promise<number>} nombre de documents supprimés
 */
export async function deleteAssignmentsForTeacherClass(teacherId, teacherClassId) {
  if (!teacherId || !teacherClassId) return 0
  const q = query(
    collection(db, ASSIGNMENTS_COLLECTION),
    where('teacherClassId', '==', teacherClassId)
  )
  const snapshot = await getDocs(q)
  const toDelete = snapshot.docs.filter((d) => d.data().teacherId === teacherId)
  let deleted = 0
  for (let i = 0; i < toDelete.length; i += BATCH_DELETE_SIZE) {
    const chunk = toDelete.slice(i, i + BATCH_DELETE_SIZE)
    const batch = writeBatch(db)
    chunk.forEach((d) => batch.delete(d.ref))
    await batch.commit()
    deleted += chunk.length
  }
  return deleted
}

function normalizeAssignmentItems(exerciseInput, options = {}) {
  const { exerciseTitle = '', exerciseThumbnail = null, items = [] } = options

  const normalizedItems = []
  const pushItem = (item, fallbackOrder = normalizedItems.length) => {
    const exerciseId = String(item?.exerciseId || '').trim()
    if (!exerciseId) return
    const title = String(item?.exerciseTitle || '').trim() || 'Exercice'
    normalizedItems.push({
      exerciseId,
      exerciseTitle: title,
      exerciseThumbnail: item?.exerciseThumbnail ?? null,
      order: Number.isFinite(item?.order) ? item.order : fallbackOrder
    })
  }

  if (Array.isArray(items) && items.length > 0) {
    items.forEach((item, index) => pushItem(item, index))
  } else if (Array.isArray(exerciseInput)) {
    exerciseInput.forEach((item, index) => {
      if (typeof item === 'string') {
        pushItem({
          exerciseId: item,
          exerciseTitle: exerciseTitle || 'Exercice',
          exerciseThumbnail
        }, index)
      } else {
        pushItem(item, index)
      }
    })
  } else {
    const exerciseId = String(exerciseInput || '').trim()
    if (exerciseId) {
      pushItem({
        exerciseId,
        exerciseTitle: exerciseTitle || 'Exercice',
        exerciseThumbnail
      }, 0)
    }
  }

  const dedup = new Map()
  normalizedItems.forEach((item) => {
    if (!dedup.has(item.exerciseId)) dedup.set(item.exerciseId, item)
  })

  return Array.from(dedup.values())
    .sort((a, b) => a.order - b.order)
    .map((item, index) => ({ ...item, order: index }))
}

function docToAssignment(d) {
  const data = d.data()
  const fallbackItem = {
    exerciseId: data.exerciseId,
    exerciseTitle: data.exerciseTitle || 'Exercice',
    exerciseThumbnail: data.exerciseThumbnail ?? null,
    order: 0
  }
  const normalizedItems = Array.isArray(data.items) && data.items.length > 0
    ? data.items
    : [fallbackItem]
  return {
    id: d.id,
    ...data,
    items: normalizedItems,
    exerciseIds: Array.isArray(data.exerciseIds) && data.exerciseIds.length > 0
      ? data.exerciseIds
      : normalizedItems.map((item) => item.exerciseId),
    isMulti: normalizedItems.length > 1
  }
}

/**
 * Crée un devoir (exercice assigné à une classe).
 * Dénormalise exerciseTitle et optionnellement exerciseThumbnail pour limiter les lectures Firestore.
 * @param {string} teacherId - uid du professeur
 * @param {string} teacherClassId - id du document teacherClasses
 * @param {string|Array<string|Object>} exerciseInput - id de l'exercice ou liste d'items
 * @param {Object} options - { title?, dueDate?, instructions?, exerciseTitle, exerciseThumbnail?, items? }
 * @returns {Promise<{ id: string }>}
 */
export async function createAssignment(teacherId, teacherClassId, exerciseInput, options = {}) {
  const { title = null, dueDate = null, instructions = null } = options
  if (!teacherId || !teacherClassId) {
    throw new Error('teacherId et teacherClassId sont requis.')
  }

  const normalizedItems = normalizeAssignmentItems(exerciseInput, options)
  if (normalizedItems.length === 0) {
    throw new Error('Veuillez sélectionner au moins un exercice.')
  }
  const primaryItem = normalizedItems[0]

  const ref = await addDoc(collection(db, ASSIGNMENTS_COLLECTION), {
    teacherId,
    teacherClassId,
    exerciseId: primaryItem.exerciseId,
    exerciseIds: normalizedItems.map((item) => item.exerciseId),
    items: normalizedItems,
    isMulti: normalizedItems.length > 1,
    title: title && String(title).trim() ? String(title).trim() : null,
    instructions: instructions && String(instructions).trim() ? String(instructions).trim() : null,
    dueDate: dueDate != null ? (dueDate instanceof Date ? dueDate : (dueDate?.toDate?.() ?? null)) : null,
    exerciseTitle: primaryItem.exerciseTitle,
    exerciseThumbnail: primaryItem.exerciseThumbnail,
    createdAt: serverTimestamp()
  })
  return { id: ref.id }
}

/**
 * Liste les devoirs du professeur (limité pour économiser les lectures).
 * @param {string} teacherId - uid du professeur
 * @param {number} maxCount - nombre max de devoirs (défaut 30)
 */
export async function getAssignmentsByTeacher(teacherId, maxCount = DEFAULT_LIST_LIMIT) {
  const q = query(
    collection(db, ASSIGNMENTS_COLLECTION),
    where('teacherId', '==', teacherId),
    orderBy('createdAt', 'desc'),
    limit(maxCount)
  )
  const snapshot = await getDocs(q)
  return snapshot.docs.map((d) => docToAssignment(d))
}

/**
 * Pagination : une page de devoirs du professeur.
 * @param {string} teacherId - uid du professeur
 * @param {Object} options - { pageSize?: number, startAfterDoc?: DocumentSnapshot, endBeforeDoc?: DocumentSnapshot }
 * @returns {Promise<{ assignments: Array, firstDoc: DocumentSnapshot|null, lastDoc: DocumentSnapshot|null, hasMore: boolean }>}
 */
export async function getAssignmentsByTeacherPage(teacherId, options = {}) {
  const { pageSize = 20, startAfterDoc = null, endBeforeDoc = null } = options
  const constraints = [
    collection(db, ASSIGNMENTS_COLLECTION),
    where('teacherId', '==', teacherId),
    orderBy('createdAt', 'desc')
  ]
  if (endBeforeDoc) {
    constraints.push(endBefore(endBeforeDoc), limit(pageSize))
  } else {
    if (startAfterDoc) constraints.push(startAfter(startAfterDoc))
    constraints.push(limit(pageSize))
  }
  const q = query(...constraints)
  const snapshot = await getDocs(q)
  const assignments = snapshot.docs.map((d) => docToAssignment(d))
  const firstDoc = snapshot.docs[0] || null
  const lastDoc = snapshot.docs[snapshot.docs.length - 1] || null
  const hasMore = snapshot.docs.length === pageSize
  return { assignments, firstDoc, lastDoc, hasMore }
}

/**
 * Nombre total de devoirs du professeur (pour affichage pagination).
 */
export async function getAssignmentsCountByTeacher(teacherId) {
  const q = query(
    collection(db, ASSIGNMENTS_COLLECTION),
    where('teacherId', '==', teacherId)
  )
  const snapshot = await getCountFromServer(q)
  return snapshot.data().count
}

/**
 * Liste les devoirs d'une classe (pour l'élève ou le détail prof).
 * @param {string} teacherClassId - id du document teacherClasses
 * @param {number} maxCount - nombre max (défaut 30)
 */
export async function getAssignmentsByClass(teacherClassId, maxCount = DEFAULT_LIST_LIMIT) {
  if (!teacherClassId) return []
  const q = query(
    collection(db, ASSIGNMENTS_COLLECTION),
    where('teacherClassId', '==', teacherClassId),
    orderBy('createdAt', 'desc'),
    limit(maxCount)
  )
  const snapshot = await getDocs(q)
  return snapshot.docs.map((d) => docToAssignment(d))
}

/**
 * Récupère un devoir par id.
 */
export async function getAssignmentById(assignmentId) {
  const ref = doc(db, ASSIGNMENTS_COLLECTION, assignmentId)
  const snap = await getDoc(ref)
  if (!snap.exists()) return null
  const data = snap.data()
  const fallbackItem = {
    exerciseId: data.exerciseId,
    exerciseTitle: data.exerciseTitle || 'Exercice',
    exerciseThumbnail: data.exerciseThumbnail ?? null,
    order: 0
  }
  const normalizedItems = Array.isArray(data.items) && data.items.length > 0
    ? data.items
    : [fallbackItem]
  return {
    id: snap.id,
    ...data,
    items: normalizedItems,
    exerciseIds: Array.isArray(data.exerciseIds) && data.exerciseIds.length > 0
      ? data.exerciseIds
      : normalizedItems.map((item) => item.exerciseId),
    isMulti: normalizedItems.length > 1
  }
}

/**
 * Met à jour le titre et/ou la date limite d'un devoir.
 */
export async function updateAssignment(assignmentId, teacherId, data) {
  const ref = doc(db, ASSIGNMENTS_COLLECTION, assignmentId)
  const snap = await getDoc(ref)
  if (!snap.exists() || snap.data().teacherId !== teacherId) {
    throw new Error('Devoir introuvable ou vous n\'êtes pas le propriétaire.')
  }
  const updates = { updatedAt: serverTimestamp() }
  if (data.title !== undefined) updates.title = data.title && String(data.title).trim() ? String(data.title).trim() : null
  if (data.instructions !== undefined) updates.instructions = data.instructions && String(data.instructions).trim() ? String(data.instructions).trim() : null
  if (data.dueDate !== undefined) updates.dueDate = data.dueDate != null ? (data.dueDate instanceof Date ? data.dueDate : (data.dueDate?.toDate?.() ?? null)) : null
  if (Array.isArray(data.items) && data.items.length > 0) {
    const normalizedItems = normalizeAssignmentItems(data.items, { items: data.items })
    const primaryItem = normalizedItems[0]
    updates.items = normalizedItems
    updates.exerciseIds = normalizedItems.map((item) => item.exerciseId)
    updates.isMulti = normalizedItems.length > 1
    updates.exerciseId = primaryItem.exerciseId
    updates.exerciseTitle = primaryItem.exerciseTitle
    updates.exerciseThumbnail = primaryItem.exerciseThumbnail
  }
  await updateDoc(ref, updates)
}

/**
 * Supprime un devoir.
 */
export async function deleteAssignment(assignmentId, teacherId) {
  const ref = doc(db, ASSIGNMENTS_COLLECTION, assignmentId)
  const snap = await getDoc(ref)
  if (!snap.exists() || snap.data().teacherId !== teacherId) {
    throw new Error('Devoir introuvable ou vous n\'êtes pas le propriétaire.')
  }
  await deleteDoc(ref)
}
